import { RouteObject } from 'react-router-dom';

import { authorizedLoader, isAuthenticated } from '../../utils/loaders.ts';
import { SiteLayout } from './components/Site.tsx';
import { getReceiver } from './components/messages/conversationUtils.ts';
import {
    conversationDetailLoader,
    conversationsLoader,
    eventDetailLoader,
    homeLoader,
    jobDetailLoader,
    jobListLoader,
    organizationDetailLoader,
    organizationListLoader,
    pageDetailLoader,
    sessionDetailLoader,
    sessionListLoader,
    userDetailLoader,
    userListLoader,
} from './loaders.ts';
import {
    HOME_PATH,
    SITE_CONVERSATION_ADD_PATH,
    SITE_CONVERSATION_DETAIL_PATH,
    SITE_EVENT_DETAIL_PATH,
    SITE_JOB_DETAIL_PATH,
    SITE_JOB_LIST_PATH,
    SITE_MESSAGES_PATH,
    SITE_ORGANIZATION_DETAIL_PATH,
    SITE_ORGANIZATION_LIST_PATH,
    SITE_PAGE_DETAIL_PATH,
    SITE_SESSIONS_DETAIL_PATH,
    SITE_SESSIONS_LIST_PATH,
    SITE_USER_DETAIL_PATH,
    SITE_USER_LIST_PATH,
    STATIC_PAGE_LEGAL_NOTICE_PATH,
    STATIC_PAGE_PRIVACY_PATH,
    STATIC_PAGE_TERMS_PATH,
} from './paths.ts';
import { isCustomDomain } from './utils.ts';

// /events/:eventId

export default [
    !isCustomDomain()
        ? {
              handle: {
                  title: ['site:homePage.documentTitle'],
              },
              path: HOME_PATH,
              lazy: async () => ({ Component: (await import('./pages/HomePage.tsx')).default }),
              loader: homeLoader,
          }
        : null,
    {
        handle: {
            title: ['site:eventDetailPage.documentTitle'],
        },
        path: SITE_EVENT_DETAIL_PATH,
        lazy: async () => ({ Component: (await import('./pages/EventDetailPage.tsx')).default }),
        loader: eventDetailLoader,
    },

    {
        element: <SiteLayout />,
        loader: eventDetailLoader,
        children: [
            {
                handle: {
                    title: ['site:staticPages.terms.documentTitle'],
                    hideNavigation: true,
                },
                path: STATIC_PAGE_TERMS_PATH,
                lazy: async () => {
                    const Component = (await import('./pages/StaticPage.tsx')).default;
                    return { Component: () => <Component pageName="terms" /> };
                },
            },
            {
                handle: {
                    title: ['site:staticPages.privacy.documentTitle'],
                    hideNavigation: true,
                },
                path: STATIC_PAGE_PRIVACY_PATH,
                lazy: async () => {
                    const Component = (await import('./pages/StaticPage.tsx')).default;
                    return { Component: () => <Component pageName="privacy" /> };
                },
            },
            {
                handle: {
                    title: ['site:staticPages.legalNotice.documentTitle'],
                    hideNavigation: true,
                },
                path: STATIC_PAGE_LEGAL_NOTICE_PATH,
                lazy: async () => {
                    const Component = (await import('./pages/StaticPage.tsx')).default;
                    return { Component: () => <Component pageName="legalNotice" /> };
                },
            },
            {
                handle: {
                    title: ['site:organizationListPage.documentTitle'],
                },
                path: SITE_ORGANIZATION_LIST_PATH,
                lazy: async () => ({ Component: (await import('./pages/OrganizationListPage.tsx')).default }),
                loader: organizationListLoader,
            },
            {
                handle: {
                    title: (data: any) =>
                        data?.organization?.name
                            ? ['site:organizationDetailPage.documentTitle', { name: data.organization.name }]
                            : ['site:organizationDetailPage.documentTitleFallback'],
                },
                path: SITE_ORGANIZATION_DETAIL_PATH,
                lazy: async () => ({ Component: (await import('./pages/OrganizationDetailPage.tsx')).default }),
                loader: organizationDetailLoader,
            },
            {
                handle: {
                    title: ['site:userListPage.documentTitle'],
                },
                path: SITE_USER_LIST_PATH,
                lazy: async () => ({ Component: (await import('./pages/UserListPage.tsx')).default }),
                loader: userListLoader,
            },
            {
                handle: {
                    title: (data: any) =>
                        data?.user?.firstName || data?.user?.lastName
                            ? [
                                  'site:userDetailPage.documentTitle',
                                  {
                                      firstName: data.user.firstName,
                                      lastName: data.user.lastName,
                                  },
                              ]
                            : ['site:userDetailPage.documentTitleFallback'],
                },
                path: SITE_USER_DETAIL_PATH,
                lazy: async () => ({ Component: (await import('./pages/UserDetailPage.tsx')).default }),
                loader: userDetailLoader,
            },
            {
                handle: {
                    title: ['site:jobListPage.documentTitle'],
                },
                path: SITE_JOB_LIST_PATH,
                lazy: async () => ({ Component: (await import('./pages/JobListPage.tsx')).default }),
                loader: jobListLoader,
            },
            {
                handle: {
                    title: (data: any) =>
                        data?.job?.title
                            ? ['site:jobDetailPage.documentTitle', { title: data.job.title }]
                            : ['site:jobDetailPage.documentTitleFallback'],
                },
                path: SITE_JOB_DETAIL_PATH,
                lazy: async () => ({ Component: (await import('./pages/JobDetailPage.tsx')).default }),
                loader: jobDetailLoader,
            },
            {
                handle: {
                    title: ['site:sessionListPage.documentTitle'],
                },
                path: SITE_SESSIONS_LIST_PATH,
                lazy: async () => ({ Component: (await import('./pages/SessionListPage.tsx')).default }),
                loader: sessionListLoader,
            },
            {
                handle: {
                    title: (data: any) =>
                        data?.eventSession?.title
                            ? ['site:sessionDetailPage.documentTitle', { title: data.eventSession.title }]
                            : ['site:sessionDetailPage.documentTitleFallback'],
                },
                path: SITE_SESSIONS_DETAIL_PATH,
                lazy: async () => ({ Component: (await import('./pages/SessionDetailPage.tsx')).default }),
                loader: sessionDetailLoader,
            },
            {
                handle: {
                    title: (data: any) => {
                        const pageTitle = data?.page?.[0]?.title || data?.page?.title || null;
                        return pageTitle
                            ? ['site:pageDetailPage.documentTitle', { title: pageTitle }]
                            : ['site:pageDetailPage.documentTitleFallback'];
                    },
                },
                path: SITE_PAGE_DETAIL_PATH,
                lazy: async () => ({ Component: (await import('./pages/PageDetailPage.tsx')).default }),
                loader: pageDetailLoader,
            },
            {
                handle: {
                    title: ['site:messagesPage.documentTitle'],
                    hideNavigation: true,
                    hideFooter: true,
                },
                path: SITE_MESSAGES_PATH,
                lazy: async () => ({ Component: (await import('./pages/MessagesPage.tsx')).default }),
                loader: authorizedLoader(conversationsLoader, isAuthenticated),
                children: [
                    {
                        handle: {
                            title: ['site:messagesPage.newMessageDocumentTitle'],
                            hideNavigation: true,
                            hideFooter: true,
                        },
                        path: SITE_CONVERSATION_ADD_PATH,
                        lazy: async () => ({
                            Component: (await import('./components/messages/AddConversationFormDialog.tsx'))
                                .AddConversationFormDialog,
                        }),
                    },
                ],
            },
            {
                handle: {
                    title: (data: any) => {
                        const receiver = getReceiver(data?.conversation, data?.user);
                        return receiver?.firstName || receiver?.lastName
                            ? [
                                  'site:messagesPage.conversationDocumentTitle',
                                  {
                                      firstName: receiver?.firstName,
                                      lastName: receiver?.lastName,
                                  },
                              ]
                            : ['site:messagesPage.conversationDocumentTitleFallback'];
                    },
                    hideNavigation: true,
                    hideFooter: true,
                },
                path: SITE_CONVERSATION_DETAIL_PATH,
                lazy: async () => ({ Component: (await import('./pages/MessagesPage.tsx')).default }),
                loader: authorizedLoader(conversationDetailLoader, isAuthenticated),
            },
        ],
    },
].filter(Boolean) as RouteObject[];
